/**
 * Admin Orca-archive RPC definitions (OL-152) — the OPERATOR door.
 *
 * Three methods over the same archived objects the customer door exposes, plus
 * the one thing the customer door deliberately lacks: `downloadUrl`, which
 * hands back a pre-signed URL to an object's RAW bytes. Raw bytes are
 * admin-only, so that method exists on this group and nowhere else.
 *
 * Because an operator is already trusted with the whole fleet, this door is
 * allowed to be an existence oracle: `RecordNotFoundError` is a legitimate
 * answer to "read that object" and distinguishes "wrong object name" from
 * "archive unavailable". The customer door makes the opposite choice — see
 * `rpc/authenticated/orcaloops-archive.rpc.ts`.
 *
 * {@link ArchiveNotReady} is a PERMANENT member of every union here, not a
 * scaffold: it is also the honest error when the reader has no AWS
 * configuration. Filling in a stub handler is not a wire change.
 *
 * Every schema comes from `../orca-archive.schemas.ts`; this file declares none
 * of its own (both groups are `export *`-ed into `rpc/index.ts`, so a
 * same-named export in each would be a TS2308 duplicate).
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { DatabaseError, Forbidden, RecordNotFoundError, Unauthorized } from "../errors.ts";
import {
  ArchiveListInput,
  ArchiveNotReady,
  ArchiveObjectNotRenderable,
  ArchiveObjectRef,
  ArchiveObjectTooLarge,
  ArchiveTextInput,
  OrcaArchiveDownloadUrl,
  OrcaArchiveListing,
  OrcaArchiveText,
} from "../orca-archive.schemas.ts";

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Orca-archive RPC group. Gated by AdminMiddleware per `Rpc.make` —
 * never on the group — matching every other `admin.orca*` group.
 *
 * @since 1.0.0
 */
export class AdminOrcaArchiveRpcs extends RpcGroup.make(
  Rpc.make("admin.orcaArchive.list", {
    payload: ArchiveListInput,
    success: OrcaArchiveListing,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError, ArchiveNotReady),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.orcaArchive.text", {
    payload: ArchiveTextInput,
    success: OrcaArchiveText,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      DatabaseError,
      ArchiveNotReady,
      RecordNotFoundError,
      ArchiveObjectTooLarge,
      ArchiveObjectNotRenderable,
    ),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.orcaArchive.downloadUrl", {
    payload: ArchiveObjectRef,
    success: OrcaArchiveDownloadUrl,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError, ArchiveNotReady, RecordNotFoundError),
  }).middleware(AdminMiddleware),
) {}
